import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { guestApi } from '@/api/guest';
import type { UtilityType } from '@/api/utilities';

interface Form {
  type: UtilityType;
  value: string;
}

export function GuestReadingsPage() {
  const { t } = useTranslation();
  const params = useParams<{ leaseId: string; month: string; year: string }>();
  const leaseId = Number(params.leaseId);
  const month = Number(params.month);
  const year = Number(params.year);
  const { register, handleSubmit } = useForm<Form>({ defaultValues: { type: 'electricity' } });
  const [photo, setPhoto] = useState<File | null>(null);

  const submit = useMutation({
    mutationFn: (form: FormData) => guestApi.submitReading(leaseId, month, year, form),
  });

  const onSubmit = handleSubmit(async (data) => {
    const form = new FormData();
    form.append('type', data.type);
    form.append('value', data.value);
    if (photo) form.append('photo', photo);
    await submit.mutateAsync(form);
  });

  if (submit.isSuccess) {
    return (
      <div className="mx-auto max-w-xl p-4">
        <Card title={t('utilities.submitted')}>
          <p className="text-sm text-green-700">{t('utilities.submitted_body')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <Card title={t('utilities.submit_reading')}>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block text-sm">
            {t('utilities.type')}
            <select {...register('type')} className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2">
              <option value="electricity">{t('utilities.types.electricity')}</option>
              <option value="water_cold">{t('utilities.types.water_cold')}</option>
              <option value="water_hot">{t('utilities.types.water_hot')}</option>
              <option value="gas">{t('utilities.types.gas')}</option>
            </select>
          </label>
          <Input label={t('utilities.value')} type="number" step="0.01" {...register('value', { required: true })} />
          <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
          <Button type="submit" className="w-full" loading={submit.isPending}>
            {t('common.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
